'use client';

import { useContext, useEffect, useRef, useState } from 'react';
import { FieldError, get, useFormContext } from 'react-hook-form';
import ReactInputMaskBase from 'react-input-mask';
import { twMerge } from 'tailwind-merge';

import { PropsWithClasses } from '@lamesarv-sdk/types';

import { useEnv } from '@/env/client';

import { CommonFormContext } from '.';

// Review inconsistency with Typescript definitions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ReactInputMask = ReactInputMaskBase as unknown as React.FC<any>;

interface IPhoneCountry {
  code: string;
  flag: string;
  mask: string;
  dialCode: string;
}

// Digit counts are equal (10 national digits) between countries — the masks
// only differ in grouping to match each country's common representation. The
// dial code is never shown in the mask (selecting the flag already conveys
// it, same as US doesn't show "+1") — it's prefixed onto the submitted value
// instead, so parsePhone can tell the countries apart.
const PHONE_COUNTRIES: IPhoneCountry[] = [
  { code: 'US', flag: '🇺🇸', mask: '(999) 999-9999', dialCode: '+1' },
  { code: 'MX', flag: '🇲🇽', mask: '99 9999 9999', dialCode: '+52' },
];

// The submitted value is "{dialCode}{national digits}" with no separators, so
// its total digit count pins down whether a full national number was entered
// — unlike phoneRegex's 7-15 digit range, which is too loose to tell a
// complete number from one still a digit short. Consumers gating UI on "did
// the user finish typing a phone number" (e.g. the SMS opt-in checkbox)
// should use this instead of re-deriving their own digit-count heuristic.
// The dial code must match first — otherwise a partial MX number can land on
// the same total digit count as a complete US one (and vice versa) and read
// as "complete" for the wrong country.
export const isPhoneNumberComplete = (value?: string) => {
  const trimmedValue = (value || '').trim();
  const country = PHONE_COUNTRIES.find((item) => trimmedValue.startsWith(item.dialCode));

  if (!country) return false;

  const digitCount = trimmedValue.replace(/\D/g, '').length;
  const dialDigits = country.dialCode.replace(/\D/g, '').length;
  const nationalDigits = country.mask.replace(/[^9]/g, '').length;

  return digitCount === dialDigits + nationalDigits;
};

interface ICommonPhoneInputProps extends PropsWithClasses {
  name: string;
  label?: string;
  hideLabel?: boolean;
  isRequired?: boolean;
  placeholder?: string;
  classContainer?: string;
  classLabel?: string;
  classLabelWithValue?: string;
  classInputWithValue?: string;
  isFloatingLabel?: boolean;
}

const SELECT_WIDTH_CLASS = 'w-11';
const SELECT_WIDTH_REM = '2.75rem'; // keep in sync with SELECT_WIDTH_CLASS above

// Some CMS-authored page CSS (custom code on promo-page embeds) forces a
// fixed box — width/min-height/padding/border/border-radius, all !important
// — onto every bare <input>, <select> and <textarea> under the embed. That's
// a safe assumption when each form field is exactly one such element (true
// for every other field), but this one splits a field into a select+input
// pair inside its own bordered wrapper, so the same rule lands on the inner
// elements too and re-boxes/re-widens them independently (the input's own
// flex-basis:0 happens to keep the forced width from affecting it, but the
// select — sized by flex-basis:auto — visibly stretches to it). An external
// stylesheet's !important can only be beaten by an inline !important — but
// React's style prop silently drops the "!important" suffix for most
// properties (only carries through for a few, e.g. minHeight), so it has to
// be set for real via the CSSOM's setProperty(..., 'important'), on the node.
const resetCmsFieldBox = (width?: string) => (el: HTMLElement | null) => {
  const props: [string, string][] = [
    ['min-height', '0'],
    ['padding-top', '0'],
    ['padding-right', '0'],
    ['padding-bottom', '0'],
    ['padding-left', '0'],
    ['border-width', '0'],
    ['border-top-left-radius', '0'],
    ['border-top-right-radius', '0'],
    ['border-bottom-left-radius', '0'],
    ['border-bottom-right-radius', '0'],
    ...(width ? ([['width', width]] as [string, string][]) : []),
  ];

  props.forEach(([property, value]) => el?.style.setProperty(property, value, 'important'));
};
// Clears the select column so the resting (large, placeholder-style) label
// doesn't sit on top of the flag. Only applied at rest — once shrunk, the
// label is a small tag near the top-left corner like every other field's,
// clear of the flag either way, so classLabelWithValue's own left applies.
const FLOATING_LABEL_RESTING_LEFT = 'left-16';

export const CommonPhoneInput = (props: ICommonPhoneInputProps) => {
  const { env } = useEnv();
  const countries = env.ENABLE_INTERNATIONAL_PHONE_INPUT ? PHONE_COUNTRIES : PHONE_COUNTRIES.slice(0, 1);

  const [country, setCountry] = useState(countries[0]);
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputElRef = useRef<HTMLInputElement | null>(null);

  const {
    register,
    trigger,
    clearErrors,
    watch,
    setValue: setFormValue,
    formState: { errors },
  } = useFormContext();
  const { isDisabled } = useContext(CommonFormContext);

  // The visible field shows a masked, country-local number (e.g. "(898) 989-8989"),
  // decoupled from the value submitted to the form (e.g. "+18989898989"), which
  // carries the dial code so parsePhone can tell countries apart. Feeding the
  // dial-coded value back through react-input-mask's own onChange would make it
  // re-mask its own already-masked output — mixing dial-code digits into what the
  // user typed — so the two never share the same field/DOM node.
  register(props.name);

  // isSubmitSuccessful only means handleSubmit's callback didn't throw — these
  // forms catch their own API errors internally and never rethrow, so it stays
  // true even when the actual submission failed and the user still needs their
  // number. The form's own reset({ [name]: '' }) after a *genuine* success is
  // the real signal — watch for the RHF value going externally empty (i.e. not
  // via our own commitValue, which already keeps displayValue in lockstep).
  const formValue = watch(props.name);

  useEffect(() => {
    if (formValue || !displayValue) return;

    setDisplayValue('');
    setCountry(PHONE_COUNTRIES[0]);
  }, [formValue]);

  const error = get(errors, props.name) as FieldError | null;
  // Floating-label consumers style the whole box via classInput/classInputWithValue
  // (normally applied to a single <input>, keyed off focus/value). Since this field
  // splits that box into a country select + masked input, we track focus ourselves
  // and apply classInputWithValue to the box directly so the same floating effect works.
  const isActive = !!displayValue || isFocused;

  const commitValue = (rawDisplayValue: string) => {
    setDisplayValue(rawDisplayValue);

    const digits = rawDisplayValue.replace(/\D/g, '');

    setFormValue(props.name, digits ? `${country.dialCode}${digits}` : '', {
      shouldValidate: false,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const label = !props.hideLabel && (
    <span
      className={twMerge(
        'text-sm text-black-500',
        props.classLabel,
        props.isFloatingLabel && countries.length > 1 && !isActive && FLOATING_LABEL_RESTING_LEFT,
        isActive && props.classLabelWithValue,
      )}
    >
      {props.label}
      {props.isRequired && <span className="ml-1 text-red-700 leading-none">*</span>}
    </span>
  );

  return (
    <div className={twMerge('flex flex-col gap-1.5', isDisabled && 'opacity-60', props.classContainer)}>
      {!props.isFloatingLabel && label}
      <div
        className={twMerge(
          'flex items-center gap-1.5 border border-black-200 rounded-sm px-3 py-2 w-full',
          props.isFloatingLabel && 'relative',
          props.classInput,
          isActive && props.classInputWithValue,
          error && 'border-red-700',
        )}
      >
        {props.isFloatingLabel && label}
        {countries.length > 1 && (
          <select
            aria-label="Country"
            className={twMerge(
              // Native form controls don't reliably inherit line-height from
              // ancestors — without forcing it, this falls back to the
              // browser default (1.5) instead of the wrapping box's actual
              // line-height (e.g. leading-none), inflating the box's height.
              'shrink-0 appearance-none bg-none bg-transparent border-none p-0 leading-[inherit] text-xl text-center focus:outline-none focus:ring-0',
              SELECT_WIDTH_CLASS,
            )}
            ref={resetCmsFieldBox(SELECT_WIDTH_REM)}
            value={country.code}
            disabled={isDisabled}
            onChange={(event) => {
              const nextCountry = countries.find((item) => item.code === event.target.value) ?? countries[0];

              setCountry(nextCountry);
              setDisplayValue('');
              setFormValue(props.name, '', { shouldValidate: false, shouldDirty: true, shouldTouch: true });

              // The input may still be focused from before the switch (a real
              // click on the select blurs it first, but keyboard-driven or
              // programmatic changes may not) — react-input-mask shows its
              // placeholder template while focused-and-empty and gets its
              // cursor tracking confused switching mask+value at once while
              // focused, so force it unfocused before the mask actually changes.
              inputElRef.current?.blur();
            }}
          >
            {countries.map((item) => (
              <option key={item.code} value={item.code}>
                {item.flag}
              </option>
            ))}
          </select>
        )}
        <ReactInputMask
          inputRef={(el: HTMLInputElement | null) => {
            inputElRef.current = el;
            resetCmsFieldBox()(el);
          }}
          name={props.name}
          value={displayValue}
          type="tel"
          mask={country.mask}
          placeholder={props.placeholder}
          required={props.isRequired}
          disabled={isDisabled}
          className={twMerge(
            // See the select's className above — same non-inheritance issue.
            'flex-1 bg-transparent border-none p-0 leading-[inherit] placeholder-black-350 text-black-500 focus:outline-none focus:ring-0',
            error && 'placeholder-red-400 text-red-700',
          )}
          onKeyDown={async (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
              setTimeout(() => {
                clearErrors(props.name);
                trigger(props.name);
              }, 10);
            }
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => commitValue(event.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            trigger(props.name);
          }}
        />
      </div>
      {error && <span className="text-left text-red-700 text-sm">{error.message}</span>}
    </div>
  );
};
